import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, CreditCard, XCircle } from "lucide-react";
import { PRICE, PLAN_LOOKUP_KEY } from "@/lib/pricing";

const PLAN_COLUMNS = [
  {
    title: "Create & Record",
    items: [
      "AI host casting — 8 looks, standing or seated",
      "Lifelike AI voices + auto commentary from the transcript",
      "Side-by-side recorder, unlimited takes",
      "15 studio backdrops, AI generator & custom uploads",
      "Live captions with timestamps + srt export",
      "AI thumbnails — 3 bold options per take",
    ],
  },
  {
    title: "Publish & Grow",
    items: [
      "One-click YouTube upload with scheduling",
      "Unlimited AI titles, descriptions, tags & chapters",
      "Shorts & highlight auto-detection with caption burn-in",
      "Trending research by region & category",
      "Public share links with view counts",
      "Fair-use credit added automatically",
    ],
  },
];

export default function Billing() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [cancelAtEnd, setCancelAtEnd] = useState(false);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [busy, setBusy] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadStatus = () =>
    api.get("/billing/status").then((r) => {
      setStatus(r.data.subscription_status);
      setCancelAtEnd(!!r.data.cancel_at_period_end);
      setPeriodEnd(r.data.current_period_end || null);
    }).catch(() => {});

  useEffect(() => { loadStatus(); }, []);

  const purchase = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: PLAN_LOOKUP_KEY,
        origin_url: window.location.origin,
        user_id: user?.user_id,
      });
      window.location.href = r.data.checkout_url;
    } catch {
      toast.error("Could not start checkout");
      setBusy(false);
    }
  };

  const cancelSubscription = async () => {
    setCanceling(true);
    try {
      const r = await api.post("/payments/cancel", {});
      setCancelAtEnd(true);
      setPeriodEnd(r.data.current_period_end || null);
      setConfirmOpen(false);
      toast.success("Your plan will cancel at the end of the billing period");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setCanceling(false);
    }
  };

  const periodEndLabel = periodEnd
    ? new Date(periodEnd * 1000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <AppShell>
      <div className="p-10">
        <h1 className="font-display font-bold text-3xl tracking-tight mb-1 text-zinc-900">Billing</h1>
        <p className="text-zinc-500 text-sm mb-10">One plan. Everything in it.</p>
        <div className="border border-zinc-800 rounded-[4px] p-8 bg-zinc-950 text-white shadow-sm md:w-1/3">
          <div className="mb-8">
            <h2 className="font-display font-bold text-2xl tracking-tight text-white">Creator Plan: One Channel</h2>
            <div className="flex items-center gap-3 mt-3">
              <span className="font-display font-extrabold text-2xl text-white">Only {PRICE}/ month</span>
              {status === "active" && <span className="text-emerald-400 text-xs border border-emerald-500/40 bg-emerald-500/10 rounded-[4px] px-3 py-1" data-testid="active-badge">Active</span>}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-y-8 mb-8" data-testid="plan-feature-columns">
            {PLAN_COLUMNS.map((col) => (
              <div key={col.title}>
                <div className="text-[11px] uppercase tracking-widest text-orange-500 mb-3">{col.title}</div>
                <div className="space-y-2.5">
                  {col.items.map((p) => (
                    <div key={p} className="flex items-start gap-2.5 text-sm text-zinc-300"><Check size={15} className="text-emerald-500 mt-0.5 shrink-0" /> {p}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {status !== "active" && (
            <button onClick={purchase} disabled={busy} data-testid="billing-purchase-btn"
              className="w-full bg-white hover:bg-zinc-100 disabled:opacity-50 text-zinc-900 font-medium rounded-[4px] py-3 transition-colors flex items-center justify-center gap-2">
              <CreditCard size={15} /> {busy ? "Redirecting…" : "Subscribe with Stripe"}
            </button>
          )}
          {status === "active" && cancelAtEnd && (
            <div data-testid="cancel-scheduled-note" className="w-full text-center text-amber-400 text-xs border border-amber-500/40 bg-amber-500/10 rounded-[4px] py-3 px-4">
              Your plan is set to cancel{periodEndLabel ? ` on ${periodEndLabel}` : " at the end of the billing period"}. You keep full access until then.
            </div>
          )}
          {status === "active" && !cancelAtEnd && (
            <button onClick={() => setConfirmOpen(true)} data-testid="billing-cancel-btn"
              className="w-full bg-transparent hover:bg-zinc-900 text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 font-medium rounded-[4px] py-3 transition-colors flex items-center justify-center gap-2">
              <XCircle size={15} /> Cancel subscription
            </button>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-4 md:w-1/3">Bring your own OpenAI API key (added in Settings) — all AI generation is billed to you by OpenAI. This plan covers the platform.</p>
        <p className="text-[11px] text-zinc-500 mt-4">Test mode: card 4242 4242 4242 4242, any future expiry, any CVC.</p>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="cancel-confirm-modal">
          <div className="bg-zinc-950 border border-zinc-800 rounded-[6px] p-6 w-full max-w-md text-white">
            <h3 className="font-display font-bold text-lg mb-2">Cancel your Creator Plan?</h3>
            <p className="text-sm text-zinc-400 mb-6">
              Your subscription will stop renewing and cancel at the end of your current billing period. You'll keep full access until then — no refund for the current period.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmOpen(false)} disabled={canceling} data-testid="cancel-dismiss-btn"
                className="px-4 py-2 text-sm text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded-[4px] transition-colors">
                Keep plan
              </button>
              <button onClick={cancelSubscription} disabled={canceling} data-testid="cancel-confirm-btn"
                className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-[4px] transition-colors">
                {canceling ? "Canceling…" : "Yes, cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
